import * as React from 'react';

import {DraggableList} from '../draggable-row/draggable-list';
import {reorder} from '../draggable-row/helpers';
import {LockIcon, ArrowUpDownIcon} from '../icon';
import {StackLegacy} from '../layout/stack/stack-legacy';

import {getFieldKey, getTitle} from './helpers';
import {OrderLabel} from './order-label';
import {ReorderableListItem} from './reorderable-list-item';
import type {EntityFormType, SelectableField} from './types';

type Props = {
    schemaFields: any[];
    selectedFields: SelectableField[];
    onReorderFields: (fieldOrder: string[]) => void;
    onSetRequired: (arg1: SelectableField) => void;
    entityType: EntityFormType;
    canRequireFields?: boolean;
    isLeadCard?: boolean;
};

export function OrderFields(props: Props) {
    const {canRequireFields = true, selectedFields, onReorderFields, schemaFields} = props;

    const fields = selectedFields.reduce(
        // @ts-expect-error
        (acc, currentField) => {
            const schemaField = schemaFields.find((f) => currentField.fieldId === getFieldKey(f));
            if (schemaField && currentField.isRemoveable && schemaField.name !== 'priority') {
                return {
                    immutable: acc.immutable,
                    reorderable: [
                        ...acc.reorderable,
                        {
                            ...schemaField,
                            isRequired: currentField.isRequired,
                            isRemoveable: currentField.isRemoveable,
                        },
                    ],
                };
            } else if (schemaField) {
                return {
                    immutable: [
                        ...acc.immutable,
                        {
                            ...schemaField,
                            isRequired: currentField.isRequired,
                            isRemoveable: currentField.isRemoveable,
                        },
                    ],
                    reorderable: acc.reorderable,
                };
            }

            return acc;
        },
        {immutable: [], reorderable: []}
    );

    // @ts-expect-error
    const immutableListItems = fields.immutable.map((field) => {
        return (
            <ReorderableListItem
                key={field.name}
                title={getTitle(field.name, field.title, props.entityType, props.isLeadCard)}
                isRequireable={canRequireFields && field.isRequireable}
                isRequired={field.isRequired}
                isStatic={true}
                onSetRequired={(value: boolean) =>
                    props.onSetRequired({
                        __typename: 'SelectableField',
                        fieldId: getFieldKey(field),
                        isRequired: value,
                        isRemoveable: field.isRemoveable,
                    })
                }
            />
        );
    });

    // @ts-expect-error
    const reorderableListItems = fields.reorderable.map((field) => {
        return {
            id: getFieldKey(field),
            component: (
                <ReorderableListItem
                    title={getTitle(field.name, field.title, props.entityType, props.isLeadCard)}
                    isRequireable={canRequireFields && field.isRequireable}
                    isRequired={field.isRequired}
                    onSetRequired={(value: boolean) =>
                        props.onSetRequired({
                            __typename: 'SelectableField',
                            fieldId: getFieldKey(field),
                            isRequired: value,
                            isRemoveable: field.isRemoveable,
                        })
                    }
                />
            ),
        };
    });

    return (
        <div className='pad-16'>
            <StackLegacy spacing={8}>
                {immutableListItems.length === 0 ? null : (
                    <div>
                        <OrderLabel label='Fixed' icon={LockIcon} />
                        <div>{immutableListItems}</div>
                    </div>
                )}
                <div>
                    {immutableListItems.length === 0 ? null : (
                        <OrderLabel label='Reorderable' icon={ArrowUpDownIcon} alignCenter={true} />
                    )}
                    <DraggableList
                        listItems={reorderableListItems}
                        handleReorder={(startIndex, endIndex) => {
                            // @ts-expect-error
                            const immutableFieldIds = fields.immutable.map((f) => f.name);
                            const reorderableFieldIds = reorder(
                                reorderableListItems,
                                startIndex,
                                endIndex
                            ).map((f) => f.id);

                            onReorderFields(immutableFieldIds.concat(reorderableFieldIds));
                        }}
                        isInModal={true}
                    />
                </div>
            </StackLegacy>
        </div>
    );
}
